import { html, css } from "lit";
import { MenuItem, ColorTypes, IconSpec } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeModel, NodeSource, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, BrowserOptions, RequestParamsWhere, RequestParamsWhereExpr, formatDateTime } from "@reporting/rp-app-host";
import { FilterOptions, FILTER_DEFAULT_OPTIONS, REQUEST_PRIORITIES, REQUEST_TYPES, REQUEST_STATUSES } from "../types";
import { Accounting, AccountingRights } from '@reporting/rp-types/core';
import { Ticket } from '@reporting/rp-types/helpdesk';

const ticketPriorityColors: Record<number, string> = {
	// the flag is solid: the gray border color would melt into the dark background, a muted text color reads in both themes
	1: `color-mix(in srgb, var(--og-text-color), transparent 45%)`,
	2: `var(--og-colortype-${ColorTypes.Yellow}-border-color)`,
	3: `var(--og-colortype-${ColorTypes.Red}-border-color)`
};

/** Default filter: the open requests of the companies in the current group (where the user has the helpdesk right). */
async function createDefaultFilter(): Promise<FilterOptions> {
	const g = rp.accountingProvider.accounting?.id_accounting_group;
	// explicit group: without it an SA gets the companies of all the groups
	const accountings = g == null ? null : await rp.ms('accounting').get<Accounting[]>(`accountings/${g}`, {
		fields: ['accounting'],
		right: AccountingRights.Helpdesk,
	});
	return {
		...FILTER_DEFAULT_OPTIONS,
		// an empty list would match nothing - without companies the request list is limited by the rights only
		...(accountings?.length ? { accounting: accountings.map(a => a.accounting) } : {}),
	};
}

export class TicketBrowserAdapter extends BrowserAdapter {

	/** null = the default filter, resolved on the next load (it depends on the current group) */
	private _activeFilter: FilterOptions = null;
	set activeFilter(activeFilter: FilterOptions) {	this._activeFilter = activeFilter; }

	async getActiveFilter(): Promise<FilterOptions> {
		return this._activeFilter ??= await createDefaultFilter();
	}

	clearFilter() {
		this._activeFilter = null;
	}

	getTooltip(node: NodeModel) {
		if (!node.properties?.ticket) return null;
		const ticket = node.properties.ticket as Ticket;
		return html`
			<div>
				ID: ${ticket.id_request}<br>
				${msg('Priority')}: ${msg(REQUEST_PRIORITIES.find(val => val.k === ticket.priority)?.v)}<br>
				${msg('Type')}: ${msg(REQUEST_TYPES.find(val => val.k === ticket.id_type)?.v)}<br>
				${msg('Status')}: ${msg(REQUEST_STATUSES.find(val => val.k === ticket.status)?.v)}<br>
				${msg('Created')}: ${formatDateTime(ticket.date_created)}<br>
				${ticket.date_change_status ? html`${msg('Last answer')}: ${formatDateTime(ticket.date_change_status)}<br>` : '' }
				${ticket.who_responsible_dv ? html`${msg('Responsible')}: ${ticket.who_responsible_dv}<br>` : '' }
				${ticket.escalation ? html`${msg('Escalation')}: ${msg('Yes')}` : ''}
			</div>
		`;
	}

	async getTree(): Promise<TreeSource> {
		const { date_expected, ...filter } = await this.getActiveFilter();
		const where = {
			...filter,
			// the expected date is a deadline: the requests due by that day (the plain value would match the one day)
			...(date_expected ? { date_expected: { or: [[{ op: '<=', value: date_expected }]] } as RequestParamsWhereExpr } : {}),
		} as RequestParamsWhere;
		const tickets = await rp.ms('helpdesk').get<Ticket[]>(`tickets`, { where });
		const hasMultipleAccountings = tickets.some(t => t.accounting !== tickets[0].accounting);
		const treeItems: NodeSource[] = [];
		let actAccounting: number = 0;
		tickets.forEach(ticket => {
			if (hasMultipleAccountings && ticket.accounting !== actAccounting) {
				actAccounting = ticket.accounting;
				treeItems.push({
					k: ticket.accounting.toString(),
					v: `${ticket.accounting} ${ticket.company}`,
					l: 0,
					collapsed: false,
					folder: BrowserAdapter.folderOptions
				});
			}
			treeItems.push({
				k: `helpdesk-ticket-${ticket.id_request}`,
				v: ticket.subject,
				l: hasMultipleAccountings ? 1 : 0,
				icon: {
					icon: ['fas', 'flag-pennant'], // solid - the priority color fills the flag
					size: 'lg',
					color: ticketPriorityColors[ticket.priority] || 'gray',
					statusIcon: ['fas', 'circle'],
					statusColor: ticket.who_change_status_is_sa === 1 ? `var(--og-colortype-${ColorTypes.Accent}-border-color)` : 'transparent',
				} as IconSpec,
				props: { ticket }
			});
		});
		return treeItems;
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		return node.properties?.ticket ? [
			{ key: 'open', value: msg('Open'), icon: 'book-open' },
			{ key: 'edit', value: msg('Edit'), icon: 'pen-to-square' }
		] : [];
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			search: false,
			contextMenuButton: true,
			afterRenderer: (div, node) => {
				const ticket = node.properties?.ticket as Ticket;
				if (!ticket) return;
				let borderColor = 'transparent';
				if (!ticket?.is_open)
					borderColor = `var(--og-colortype-${ColorTypes.Green}-border-color)`;
				if (ticket?.status == 5) // rejected
					borderColor = `var(--og-colortype-${ColorTypes.Red}-border-color)`;
				div.style.borderLeft = `3px solid ${borderColor}`;
			},
			customStyle: css`
				.item div.txt {
					gap: 5px;
				}
			`,
		});
	}

	async onAction(key: string, node: NodeModel) {
		const ticket = node.properties.ticket as Ticket;
		switch (key) {
			case 'open':
				rp.host.open(node.key, { title: node.value });
				break;
			case 'edit':
				rp.host.openModalSidebar({
					id: `helpdesk-editTicketForm-${ticket.id_request}`,
					caption: `${msg('Edit')}: ${ticket.subject}`
				});
				break;
		}
	}
}
